import React, { useEffect, useMemo, useState } from 'react';
import { Button, Empty, Input, InputNumber, List, message, Modal, Select } from 'antd';
import CategoryList from './CategoryList';
import ProductList from './ProductList';
import { useTranslation } from 'react-i18next';
import AdminOnly, { Aks2talOnly } from '../../components/AdminOnly';
import { Link } from 'react-router-dom';
import { pipedriveAppEnabled } from 'lib/pipedriveApp';
import { OrgStatusText } from '../../modules/orgs/interfaces';
import MachineTaskEnabled from './MachineTaskEnabled';
import { useParams } from 'react-router';
import { callPipedrive } from '../../pipedrive';
import debounce from 'lodash.debounce';

const Option = Select.Option;

type LinkableDeal = {
  id: number;
  title: string;
  orgId: number | null;
  orgName: string | null;
  ownerId: number | null;
  stageName: string | null;
  status: string | null;
  score: number | null;
};

function SettingsForm(props: any) {
  const { orgId } = useParams<{ orgId: string }>();
  const { t } = useTranslation();
  const [storageAlert, setStorageAlert] = useState(props.storageAlert);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [dealSearchTerm, setDealSearchTerm] = useState('');
  const [dealSearchLoading, setDealSearchLoading] = useState(false);
  const [dealSearchAccessRestricted, setDealSearchAccessRestricted] = useState(false);
  const [linkingDealId, setLinkingDealId] = useState<number | null>(null);
  const [linkableDeals, setLinkableDeals] = useState<LinkableDeal[]>([]);

  useEffect(() => {
    setStorageAlert(props.storageAlert);
  }, [props.storageAlert]);

  const onChange = (value: number) => {
    props.updateOrg({ unit: value });
    message.success('Indstillinger gemt');
  };

  const onChangeStatus = (value: number) => {
    props.updateOrg({ status: Number(value) });
    message.success('Indstillinger gemt');
  };

  const searchLinkableDeals = async (searchTerm: string) => {
    if (!pipedriveAppEnabled()) return;
    if (searchTerm.trim().length < 2) {
      setLinkableDeals([]);
      setDealSearchAccessRestricted(false);
      return;
    }

    try {
      setDealSearchLoading(true);
      const response: any = await callPipedrive({
        requestType: 'SEARCH_LINKABLE_DEALS',
        searchTerm,
        limit: 25,
      });
      setLinkableDeals(Array.isArray(response?.data?.data) ? response.data.data : []);
      setDealSearchAccessRestricted(Boolean(response?.data?.accessRestricted));
    } catch (error) {
      console.error(error);
      setLinkableDeals([]);
      setDealSearchAccessRestricted(false);
      message.error('Kunne ikke søge Pipedrive deals');
    } finally {
      setDealSearchLoading(false);
    }
  };

  const debouncedDealSearch = useMemo(
    () =>
      debounce((searchTerm: string) => {
        void searchLinkableDeals(searchTerm);
      }, 400),
    []
  );

  useEffect(() => {
    return () => {
      debouncedDealSearch.cancel();
    };
  }, [debouncedDealSearch]);

  const openLinkDealModal = () => {
    if (!pipedriveAppEnabled()) return;
    setLinkModalOpen(true);
    setDealSearchTerm('');
    setLinkableDeals([]);
    setDealSearchAccessRestricted(false);
  };

  const closeLinkDealModal = () => {
    setLinkModalOpen(false);
    setDealSearchTerm('');
    setLinkableDeals([]);
    setDealSearchAccessRestricted(false);
  };

  const handleSelectLinkableDeal = async (deal: LinkableDeal) => {
    if (!pipedriveAppEnabled() || !orgId) {
      return;
    }

    try {
      setLinkingDealId(deal.id);
      await callPipedrive({
        requestType: 'LINK_APP_ORG',
        appOrgId: orgId,
        dealId: deal.id,
      });
      message.success('Pipedrive deal koblet til virksomheden');
      closeLinkDealModal();
      props.refreshOrg?.();
    } catch (error) {
      console.error(error);
      message.error('Kunne ikke koble virksomheden til Pipedrive deal');
    } finally {
      setLinkingDealId(null);
    }
  };

  const linkedDealId = props.pipedrive?.dealId;
  const linkedDealTitle = props.pipedrive?.dealTitle;
  const linkedOrgName = props.pipedrive?.orgName;
  const pipedriveDealUrl = linkedDealId ? `https://app.pipedrive.com/deal/${linkedDealId}` : null;

  return (
    <div>
      <div>
        <label>
          {t('settings.storage_label')}:
          <InputNumber
            min={0}
            placeholder={t('settings.storage_placeholder')}
            value={storageAlert}
            onChange={(value) => setStorageAlert(value)}
            onBlur={() => {
              props.updateOrg({
                storageAlert: storageAlert,
              });
              message.success('Indstillinger gemt');
            }}
          />
        </label>
      </div>
      <div>
        <label>
          {t('settings.unit_label')}:
          <Select
            disabled
            placeholder="Vælg enhed"
            value={props.unit}
            onChange={onChange}
            style={{ width: '100%' }}
          >
            <Option value="l">Liter (l)</Option>
            <Option value="ml">Mililiter (ml)</Option>
            <Option value="stk">Stk.</Option>
          </Select>
        </label>
      </div>
      <AdminOnly>
        <label>
          {t('settings.status_label')}:
          <Select
            placeholder="Vælg status"
            value={props.status}
            onChange={onChangeStatus}
            style={{ width: '100%' }}
          >
            {Object.entries(OrgStatusText).map(([key, value]) => (
              <Option key={key} value={Number(key)}>{t(value)}</Option>
            ))}
          </Select>
        </label>
      </AdminOnly>

      {pipedriveAppEnabled() ? (
        <div style={{ marginTop: 16 }}>
          <label>Pipedrive deal:</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
            {pipedriveDealUrl ? (
              <a href={pipedriveDealUrl} target="_blank" rel="noopener noreferrer">
                {linkedDealTitle
                  ? `${linkedDealTitle} (#${linkedDealId})`
                  : `Deal #${linkedDealId}`}
              </a>
            ) : (
              <span>Ikke koblet</span>
            )}
            {linkedOrgName && <span>Pipedrive org: {linkedOrgName}</span>}
            <Button onClick={openLinkDealModal}>
              {pipedriveDealUrl ? 'Skift Pipedrive deal' : 'Kobl Pipedrive deal'}
            </Button>
          </div>
        </div>
      ) : (
        process.env.REACT_APP_CRM_ENABLED === 'true' && (
          <Aks2talOnly>
            <p className="mt-4">
              Koblingen til CRM vælges på lokationens Drift-fane.{' '}
              <Link
                className="underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                to="/crm"
              >
                Åbn CRM
              </Link>
            </p>
          </Aks2talOnly>
        )
      )}

      <CategoryList />
      <ProductList />
      <MachineTaskEnabled orgId={orgId} />

      <Modal
        title="Søg Pipedrive deal"
        visible={pipedriveAppEnabled() && linkModalOpen}
        onCancel={closeLinkDealModal}
        footer={null}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input.Search
            size="large"
            placeholder="Søg på deal navn"
            value={dealSearchTerm}
            onChange={(event) => {
              const nextTerm = event.target.value;
              setDealSearchTerm(nextTerm);
              debouncedDealSearch(nextTerm);
            }}
            onSearch={(searchTerm) => {
              setDealSearchTerm(searchTerm);
              void searchLinkableDeals(searchTerm);
            }}
            loading={dealSearchLoading}
            enterButton
          />
          {dealSearchAccessRestricted && (
            <span style={{ color: '#6b7280', fontSize: 12 }}>
              Viser kun deals du har adgang til i Pipedrive (egne + tildelte).
            </span>
          )}
          {dealSearchTerm.trim().length < 2 ? (
            <Empty description="Skriv mindst 2 tegn for at søge deals" />
          ) : (
            <List
              bordered
              loading={dealSearchLoading}
              dataSource={linkableDeals}
              locale={{ emptyText: 'Ingen deals fundet med din adgang' }}
              renderItem={(deal) => (
                <List.Item
                  actions={[
                    <Button
                      type="primary"
                      size="small"
                      loading={linkingDealId === deal.id}
                      onClick={() => handleSelectLinkableDeal(deal)}
                    >
                      Kobl
                    </Button>,
                  ]}
                >
                  <div>
                    <div>{deal.title}</div>
                    <div style={{ color: '#6b7280', fontSize: 12 }}>
                      #{deal.id}
                      {deal.orgName ? ` · ${deal.orgName}` : ''}
                      {deal.stageName ? ` · ${deal.stageName}` : ''}
                    </div>
                  </div>
                </List.Item>
              )}
            />
          )}
        </div>
      </Modal>
    </div>
  );
}

export default SettingsForm;
